import React from 'react';
import { HiPencilSquare } from 'react-icons/hi2';

import { Avatar } from '@/components/Avatar';
import { usePhotoUpload } from '@/components/Inputs/usePhotoUpload';
import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';

import s from './AvatarUploader.module.scss';

const AvatarUploader = ({
  image,
  onChange,
  firstName,
  lastName,
  style,
}: {
  image?: null | string;
  /** Gets the adjusted photo on Done; a returned promise is the upload (see `usePhotoUpload`). */
  onChange(file: File): Promise<unknown> | void;
  firstName: string;
  lastName: string;
  style?: React.CSSProperties;
}) => {
  const upload = usePhotoUpload({ shape: 'round', onPhoto: onChange });

  // Private backend files need the bearer token; resolve those to a blob: URL.
  const { url: displayImage } = useAuthorizedFileUrl(image);
  const shown = upload.preview ?? displayImage;

  return (
    <>
      <section
        className={s.ImageUploader}
        style={style}
        role="button"
        tabIndex={0}
        aria-label={shown ? 'Change Photo' : 'Add Photo'}
        onClick={upload.pick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            upload.pick();
          }
        }}
        {...upload.dropProps}
      >
        {shown ? (
          <img src={shown} alt="" className={s.avatarImage} draggable={false} />
        ) : (
          <Avatar
            firstName={firstName}
            lastName={lastName}
            size={typeof style?.width === 'number' ? style.width : 200}
          />
        )}
        <div className={s.imageOverlay}>
          <HiPencilSquare className={s.editIcon} aria-hidden="true" />
        </div>
        {upload.overlay}
      </section>
      {/* Outside the section: clicks in the adjust dialog bubble through the React tree. */}
      {upload.element}
    </>
  );
};

export { AvatarUploader };
export default AvatarUploader;
